// First-touch acquisition attribution.
//
// On the first page view of a visit we remember where the visitor came from (utm_* params,
// the referring site, the landing path). The record is written ONCE (first touch wins) and
// survives until the sign-up, where it is attached to the new account:
//   • email sign-up  → sent as auth metadata `acquisition` (copied into "User".acquisition
//                      by handle_new_auth_user, so it works even when the session only starts
//                      after e-mail confirmation);
//   • Google OAuth   → /auth/callback POSTs it to /api/acquisition for fresh accounts.
// Nothing here identifies the person — it is the same data GA already sees.

export type Acquisition = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  referrer?: string; // referring host only, e.g. "l.instagram.com"
  landing?: string;  // first path seen, e.g. "/ig" or "/leaderboard"
  ts: string;        // ISO timestamp of the first touch
};

const KEY = 'smt_first_touch';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const;

function clean(v: string | null | undefined, max = 80): string | undefined {
  if (!v) return undefined;
  const s = v.trim().slice(0, max);
  return s || undefined;
}

/** Record the first touch of this browser, if not recorded yet. Safe to call on every route change. */
export function captureFirstTouch(): void {
  if (typeof window === 'undefined') return;
  try {
    if (localStorage.getItem(KEY)) return;
    const params = new URLSearchParams(window.location.search);
    const acq: Acquisition = { ts: new Date().toISOString() };
    for (const k of UTM_KEYS) {
      const v = clean(params.get(k));
      if (v) acq[k] = v;
    }
    let refHost: string | undefined;
    try {
      if (document.referrer) {
        const host = new URL(document.referrer).hostname;
        if (host && host !== window.location.hostname) refHost = clean(host);
      }
    } catch { /* malformed referrer */ }
    if (refHost) acq.referrer = refHost;
    acq.landing = clean(window.location.pathname, 120);
    // A plain direct visit with no UTM and no referrer is still worth a row ("direct").
    localStorage.setItem(KEY, JSON.stringify(acq));
  } catch { /* storage unavailable — attribution is best-effort */ }
}

/** The stored first touch, or null. */
export function getFirstTouch(): Acquisition | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Acquisition) : null;
  } catch {
    return null;
  }
}

/** Attach the stored first touch to the signed-in account (server keeps the first value only). */
export async function submitFirstTouch(accessToken?: string): Promise<void> {
  const acq = getFirstTouch();
  if (!acq) return;
  try {
    await fetch('/api/acquisition', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(acq),
    });
  } catch { /* non-critical */ }
}
